import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { setSetupToken } from '../utils/setupTokenStore';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const Password = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [token, setToken] = useState('');

    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswordRules, setShowPasswordRules] = useState(false);
    const [nameError, setNameError] = useState('');
    const [passwordrequired, setPasswordrequired] = useState('');
    const passwordInputRef = useRef(null);
    const rulesRef = useRef(null);

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const tokenFromUrl = queryParams.get('token');
        if (!tokenFromUrl) {
            toast.error("Invalid or missing token.");
            navigate('/');
        } else {
            setToken(tokenFromUrl);
        }
    }, [search, navigate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                rulesRef.current &&
                !rulesRef.current.contains(event.target) &&
                passwordInputRef.current &&
                !passwordInputRef.current.contains(event.target)
            ) {
                setShowPasswordRules(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (name.trim().length < 3) {
            setNameError('Name must be at least 3 characters.');
            return;
        }

        if (!password || !confirmPassword) {
            toast.warning("Please fill all password fields.");
            return;
        }

        if (password !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        if (!isPasswordValid(password)) {
            setPasswordrequired("Password does not meet required conditions.");
            return;
        }

        try {
            const response = await axios.post(`${Base_Url}/users/complete-registration`, {
                name,
                password,
                token
            });

            // Store only display-safe data in localStorage. completeRegistration
            // doesn't set an auth cookie yet (MFA isn't verified) - it returns a
            // short-lived setup token that we hand to Authentication.js via an
            // in-memory module variable (see setupTokenStore.js), so it's never
            // written to localStorage/sessionStorage, and - unlike router state -
            // never lands in window.history.state either.
            setSetupToken(response.data?.result?.token);
            localStorage.setItem('userId', response.data?.result?.userId);

            toast.success(response.data.message || "Password set successfully!");
            navigate('/authentication');
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error("Error setting password:", error);
            }
            if (error.response) {
                const backendMsg =
                    error.response.data?.error?.message ||
                    error.response.data?.message
                toast.error(backendMsg);
            } else if (error.request) {
                toast.error("No response from server. Please check your internet connection.");
            } else {
                toast.error("Unexpected error occurred. Please try again.");
            }
        }
    };

    const isPasswordValid = (pass) => {
        const length = pass.length >= 8;
        const uppercase = /[A-Z]/.test(pass);
        const lowercase = /[a-z]/.test(pass);
        const number = /[0-9]/.test(pass);
        const specialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
        return length && uppercase && lowercase && number && specialChar;
    };

    const passwordRules = [
        { label: "At least 8 characters", test: (p) => p.length >= 8 },
        { label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
        { label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
        { label: "At least one number", test: (p) => /[0-9]/.test(p) },
        { label: "At least one special character", test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
    ];


    return (
        <div className='hold-transition login-page accent-custom'>
            <ToastContainer autoClose={3000} />
            <div className="login-box login_div">
                <div className="card card-outline card-primary">
                    <div className="card-header text-center">
                        <Link to="/" className="login_logo">
                            <img src="/assets/img/logo.png" alt='logo' border={0} />
                        </Link>
                    </div>
                    <div className="card-body d-flex flex-row flex_flow_rowwrap">
                        <div className="col-12 col-sm-12 col-md-12">
                            <h5 className="login-box-msg mb-2">Create Password</h5>

                            <form onSubmit={handlePasswordSubmit} autoComplete="off">

                                <input type="text" name="fakeusernameremembered" style={{ display: 'none' }} />
                                <input type="password" name="fakepasswordremembered" style={{ display: 'none' }} />


                                <div className="input-group mb-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Your Name"
                                        value={name}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            if (e.target.value.length >= 3) {
                                                setNameError('');
                                            }
                                        }}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fa-solid fa-user" />
                                        </div>
                                    </div>
                                </div>
                                {nameError && (
                                    <div className="text-danger mb-2" style={{ fontSize: '0.9em' }}>{nameError}</div>
                                )}

                                <div className="input-group mb-2">
                                    <input
                                        ref={passwordInputRef}
                                        type="password"
                                        className="form-control"
                                        placeholder="New Password"
                                        autoComplete="new-password"
                                        value={password}
                                        onFocus={() => {
                                            setShowPasswordRules(true);
                                            setPasswordrequired('');
                                        }}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fas fa-lock" />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    ref={rulesRef}
                                    className={`password-rules-wrapper ${showPasswordRules ? 'show' : ''}`}
                                >
                                    <ul className="mb-2" style={{ listStyle: 'none', paddingLeft: 0 }}>
                                        {passwordRules.map((rule, index) => (
                                            <li
                                                key={index}
                                                style={{
                                                    color: rule.test(password) ? 'green' : 'gray',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <i
                                                    className={`fa ${rule.test(password) ? 'fa-check-circle' : 'fa-circle'}`}
                                                    style={{ marginRight: '6px' }}
                                                ></i>
                                                {rule.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {passwordrequired && (
                                    <div className="text-danger mb-2" style={{ fontSize: '0.9em' }}>{passwordrequired}</div>
                                )}

                                <div className="input-group mb-2">
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder="Confirm New Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fas fa-lock" />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 mt-3">
                                        <button type="submit" className="btn btn-blue btn-block">Set Password</button>
                                    </div>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Password;
